import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useLocation, useSearchParams, Navigate, Link } from 'react-router-dom';
import { ClipboardList, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Pokud nas Azure callback presmeroval s ?error=…, zobrazime toast a vyciste URL.
  // (AuthContext to dela globalne pro non-login routes; tady ale uzivatel skoncil
  // primo na /login, takze obsluhujeme tady, abychom presne kontrolovali kontext.)
  useEffect(() => {
    const azureError = searchParams.get('error');
    if (!azureError) return;

    const messages: Record<string, { title: string; description?: string; variant?: 'destructive' }> = {
      azure_sso_not_configured: {
        title: 'Office 365 přihlášení zatím není nakonfigurováno',
        description: 'Kontaktujte administrátora.',
      },
      azure_unknown_user: {
        title: 'Účet neexistuje',
        description: 'Váš e-mail není registrovaný. Kontaktujte administrátora.',
        variant: 'destructive',
      },
      azure_inactive_user: {
        title: 'Účet je deaktivován',
        description: 'Kontaktujte administrátora.',
        variant: 'destructive',
      },
      azure_state_mismatch: {
        title: 'Office 365 přihlášení selhalo',
        description: 'Bezpečnostní kontrola se nezdařila, zkuste to prosím znovu.',
        variant: 'destructive',
      },
      azure_oauth_error: {
        title: 'Office 365 přihlášení bylo přerušeno',
        description: 'Microsoft přihlášení neproběhlo. Zkuste to prosím znovu.',
        variant: 'destructive',
      },
      azure_token_failed: {
        title: 'Office 365 přihlášení selhalo',
        description: 'Nepodařilo se ověřit tokeny u Microsoftu. Zkuste to prosím znovu.',
        variant: 'destructive',
      },
      azure_missing_code: {
        title: 'Office 365 přihlášení selhalo',
        description: 'Microsoft nevrátil autorizační kód.',
        variant: 'destructive',
      },
    };

    const def = messages[azureError] ?? {
      title: 'Office 365 přihlášení selhalo',
      description: 'Neznámá chyba.',
      variant: 'destructive' as const,
    };
    toast(def);

    // Vycisteni query parametru, aby se toast nezobrazoval pri kazdem reloadu.
    const next = new URLSearchParams(searchParams);
    next.delete('error');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, toast]);

  // Pokud jiz prihlasen, otoc na puvodni cestu (nebo /).
  if (!loading && user) {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Neplatné e-mailové údaje nebo heslo.');
      } else {
        setError('Přihlášení selhalo, zkuste to prosím znovu.');
        // eslint-disable-next-line no-console
        console.error('login failed:', err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAzure = () => {
    // Plno-stranovy navigate (NE fetch!) — OAuth redirect flow potrebuje aby
    // prohlizec sam nasledoval 302 na login.microsoftonline.com a pak zpet na
    // /api/auth/azure/callback. Fetch by stripoval cookies a CORS by stejne
    // odmitlo cross-origin redirect na MS.
    window.location.assign('/api/auth/azure/start');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <ClipboardList className="h-7 w-7" />
          </div>
          <CardTitle>Přihlášení do aplikace Dom</CardTitle>
          <p className="text-sm text-muted-foreground">Denní operativa montáží</p>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Heslo</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
            </div>

            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Přihlásit'}
            </Button>

            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-muted-foreground hover:underline"
              >
                Zapomněli jste heslo?
              </Link>
            </div>
          </form>

          <div className="my-4 flex items-center gap-2 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" />
            <span>nebo</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button variant="outline" className="w-full" onClick={handleAzure} type="button">
            Přihlásit přes Office 365
          </Button>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            Lokální účet pro externí firmu / Office 365 pro DPB
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
